import React, { useState, useContext, useRef, useEffect, useMemo } from 'react'
import { observer } from 'mobx-react-lite'
import { statusDisplayName, ARCHIVED_STATUS_KEY } from '../../../../../utils/statusDisplayName'
import { Droppable } from 'react-beautiful-dnd';
import InfluencerItem from './InfluencerItem';
import { Eye, EyeSlash, Pencil, ThreeDotsVertical, X } from 'react-bootstrap-icons';
import CreateNewSectionModal from './CreateNewSectionModal';
import EditSectionModal from './EditSectionModal';
import { CampaignContext } from '../../../../../contexts/CampaignContext';
import { useNavigate, useParams } from 'react-router-dom';
import axiosService from '../../../../../utils/AxiosService';
import { toastError, toastSuccess } from '../../../../FormComponents/Toasts';
import { useReadOnly } from '../../../../../contexts/ReadOnlyContext';

export default observer(function InfluencerStatusColumn({ status, addNew, dragHandleProps, onDelete }) {
  const navigate = useNavigate()
  const { id } = useParams()
  const readOnly = useReadOnly()
  const [showNewSectionModal, setShowNewSectionModal] = useState(false)
  const [showEditSectionModal, setShowEditSectionModal] = useState(false)
  const [showOptions, setShowOptions] = useState(false)
  const modalRef = useRef()

  const { authentications, campaign } = useContext(CampaignContext)
  const auths = authentications.filter(auth => auth.status_id === status?.id)

  const statusName = useMemo(() => statusDisplayName(status), [status?.name, status?.system_key])

  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      setShowOptions(false)
    }
  };

  const toggleIsVisible = () => {
    axiosService.patch(`/api/project_manager/campaigns/${id}/campaign_authentication_statuses/${status?.id}`, { campaign_authentication_status: { is_visible: !status.is_visible }})
    .then(updatedStatus => {
      status.is_visible = updatedStatus.is_visible
      toastSuccess(status.is_visible ? 'Column visible for advertiser' : 'Column is hidden from advertiser')
    })
    .catch(({ data: { error } }) => toastError(error))
  }

  useEffect(() => {
    showOptions ?
      document.addEventListener('mousedown', handleClickOutside) :
      document.removeEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showOptions])

  return (
    addNew ? (
      <div className='py-3'>
        <div className="flex justify-center items-center h-10 px-5 mb-2">
          <button
            className="flex items-center justify-center w-80 rounded-lg hover:bg-gray-100 dark:hover:bg-rb-dark-mode-lighter py-2 px-5"
            onClick={() => setShowNewSectionModal(true)}
            >
            <span
              className='font-bold text-md dark:text-white'
              style={{ color: '#143955', fontWeight: 700 }}
            >
              + Add Section
            </span>
          </button>
        </div>
        <CreateNewSectionModal isOpen={showNewSectionModal} setIsOpen={setShowNewSectionModal} />
      </div>
    ) : (
      <div className='py-5'>
        <div
          className={`h-10 flex justify-between items-center px-2 mb-2 ${readOnly ? '' : 'cursor-move'}`}
          {...dragHandleProps}
        >
          <div className='flex'>
            <h2 className="dark:text-white font-bold">{statusName}</h2>
            <h3 className='ml-2 dark:text-gray-400' style={{ color: '#A1B1BC'}}>{auths.length > 0 ? auths.length : ''}</h3>
          </div>
          <div className="flex gap-2 relative">
            <button
              onClick={toggleIsVisible}
              type="button"
              className={`dark:text-white ${readOnly ? 'cursor-default' : ''}`}
              disabled={readOnly}
            >
              {status?.is_visible ? <Eye className="text-blue-400 dark:text-white"/> : <EyeSlash className="text-gray-400 dark:text-gray-400" />}
            </button>
            {!readOnly && <button
              className='hover:bg-gray-200 dark:hover:bg-rb-dark-mode-lighter rounded-lg p-2 dark:text-white'
              type='button'
              onClick={() => setShowOptions(!showOptions)}
            >
              <ThreeDotsVertical className='text-lg'/>
            </button>}
            {
              showOptions && (
                <div
                  ref={modalRef}
                  className="absolute top-10 right-0 w-36 rounded-md bg-white dark:bg-rb-dark-mode border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter z-[999]"
                >
                  <button
                    className='p-2 w-full hover:bg-gray-200 dark:hover:bg-rb-dark-mode-lighter flex items-center gap-1 dark:text-white'
                    onClick={() => {
                      setShowEditSectionModal(true)
                      setShowOptions(false)
                    }}
                  >
                    <Pencil className='text-gray-500 dark:text-gray-300 text-lg'/>
                    Edit
                  </button>
                  {status?.system_key !== ARCHIVED_STATUS_KEY && (
                    <button
                      className='p-2 w-full hover:bg-gray-200 dark:hover:bg-rb-dark-mode-lighter flex items-center gap-1 dark:text-white'
                      onClick={() => {
                        onDelete(status?.id)
                        setShowOptions(false)
                      }}
                    >
                      <X className='text-red-500 dark:text-red-400 text-xl'/>
                      Remove list
                    </button>
                  )}
                </div>
              )
            }
            <EditSectionModal status={status} isOpen={showEditSectionModal} setIsOpen={setShowEditSectionModal} />
          </div>
        </div>
        <div
          className='flex flex-col border-2 border-gray-100 dark:border-rb-dark-mode-lighter w-80 rounded-lg p-4 mb-2 dark:bg-rb-dark-mode'
          style={{ height: 'calc(100vh - 290px)', overflowY: 'auto' }}
        >
          <Droppable droppableId={`${status?.id}`} type="influencer">
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className='mt-2 pb-2 w-full h-full'
              >
                {
                  auths.map((auth, index) => (
                    <InfluencerItem
                      key={auth.authentication_id}
                      index={index}
                      auth={auth}
                    />
                  ))
                }
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
        {!readOnly && <div className="sticky bottom-0 w-full">
          <button
            className="flex items-center justify-center w-full rounded-lg hover:bg-gray-100 dark:hover:bg-rb-dark-mode-lighter py-2 px-5"
            onClick={() => navigate(`/project_manager/influencers/campaign/${campaign.id}/status/${status?.id}`) }
          >
            <span
              className='font-bold text-md text-blue-400 dark:text-white'
            >
              + Add Influencer
            </span>
          </button>
        </div>}
      </div>
    )
  );
})
